import {
  selectOrganizationId,
  useGetIntegrationsConfigQuery,
} from '@carboncopyco/data';
import type { ShareSecurity } from '@carboncopyco/types';
import { InputTextField } from '@carboncopyco/ui';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  OpportunitiesDropdownField,
  OpportunitySelector,
  useIntegration,
} from '../Integrations';
import { SalesforceNameLogo } from '../Integrations/assets';
import { integrationVendorNameToIconMap } from '../Integrations/consts';

type ClientNameFieldProps = {
  disabled?: boolean;
};
export const ClientNameField = <S extends { security: ShareSecurity }>({
  disabled,
}: ClientNameFieldProps) => {
  const flags = useFlags();
  const organizationIdFromStore = useSelector(selectOrganizationId);
  const { organizationId: organizationIdFromParams } = useParams();
  const organizationId = organizationIdFromStore || organizationIdFromParams;
  const { values } = useFormikContext<S>();
  const {
    disabled: isSalesforceDisabled,
    renewalRequired: salesforceRenewalRequired,
    activeOpportunities: salesforceActiveOpportunities,
  } = useIntegration(
    {
      integrationType: 'salesforce',
      organizationId,
    },
    { skip: flags.hubspotCrm }
  );

  const { data: config } = useGetIntegrationsConfigQuery(
    {
      organizationId: organizationIdFromParams,
    },
    { skip: !flags.hubspotCrm }
  );
  const integrationType = config?.crm;
  const { disabled: isIntegrationDisabled, renewalRequired } = useIntegration(
    {
      integrationType,
      organizationId,
    },
    { skip: !flags.hubspotCrm }
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const opportunitiesInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    opportunitiesInputRef.current?.focus();
  }, []);

  const IntegrationLogo = useMemo(
    () => integrationVendorNameToIconMap[integrationType],
    [integrationType]
  );

  if (
    values.security.level === 'internal' ||
    values.security.level === 'anyone'
  ) {
    return null;
  }

  return !(flags.hubspotCrm ? isIntegrationDisabled : isSalesforceDisabled) ? (
    <div className="pb-5">
      {flags.hubspotCrm ? (
        <OpportunitySelector
          ref={opportunitiesInputRef}
          clientFieldName="client"
          isIntegrationConnected={!renewalRequired}
          integrationLogo={<IntegrationLogo />}
        />
      ) : (
        <OpportunitiesDropdownField
          ref={opportunitiesInputRef}
          clientFieldName="client"
          integrationName="Salesforce"
          opportunities={salesforceActiveOpportunities || []}
          opportunityListTitle="salesforce results"
          isIntegrationConnected={!salesforceRenewalRequired}
          integrationLogo={<SalesforceNameLogo />}
        />
      )}
    </div>
  ) : (
    <InputTextField
      ref={inputRef}
      required
      name="client"
      inputSize="lg"
      label="Opportunity"
      disabled={disabled}
    />
  );
};
